import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Globe, MoreHorizontal, PenLine, UserX } from 'lucide-react';
import { InstructorLesson } from '../../types';
import { instructorApi } from '../../api/instructor';
import dayjs, { fmtTime, initials } from '../../lib/dates';
import { absenceDeadline, canReportAbsence, canSignAttendance, lessonState, type LessonState } from '../../lib/lessonStatus';
import { isOfferedOpenSlot } from '../../lib/openSlot';
import { useUiStore } from '../../store/ui';
import { Badge, Button, ConfirmDialog, Menu, cx, useToast, type BadgeTone } from '../charte';
import { StudentPhoto } from '../Photos';
import PhoneLink from '../PhoneLink';
import AttendanceModal from '../AttendanceModal';
import { useLessonActions } from './useLessonActions';
import { useCloseOpenSlot, useOpenSlotsAllowed } from './OpenSlotActions';

// Carte d'une leçon (Ma journée, Planning) — refonte du 14/10/2026, tablette d'abord : à 15 minutes du début, puis
// commencée sans émargement, « Émargement » ; le reste (report, rappel, annulation) dans « ··· ». Évaluation de départ,
// rendez-vous, bilan… ne s'émargent pas : jusqu'à une heure après la fin, « Signaler une absence ».
// 30/10/2026 : le livret et le résumé de la séance ne font plus qu'une page ; « Livret et résumé de la séance » remplace
// « Préparer le cours », « Mes notes » et « Faire le résumé de la séance », toujours visible (plus derrière « ··· »).

const failure = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

const STATE_BADGE: Record<LessonState, { label: string; tone: BadgeTone }> = {
  upcoming: { label: 'Planifiée', tone: 'todo' },
  honored: { label: 'Honorée', tone: 'ok' },
  noshow: { label: 'Non honorée', tone: 'danger' },
  cancelled: { label: 'Annulée', tone: 'mute' },
  ended: { label: 'Terminé', tone: 'mute' },
  absent: { label: 'Absence signalée', tone: 'danger' },
};

const fullName = (l: InstructorLesson) => (l.candidate ? `${l.candidate.firstName} ${l.candidate.lastName}` : 'Élève');

/** Créneau à réserver : ouvert aux élèves (vert, pointillés) ou plus proposé (gris). */
export function OpenSlotCard({ lesson, showTime = false }: { lesson: InstructorLesson; showTime?: boolean }) {
  const offered = isOfferedOpenSlot(lesson);
  // « Retirer le créneau » : seulement si l'auto-école permet aux moniteurs d'ouvrir et fermer leurs créneaux.
  const { entries, dialogs } = useCloseOpenSlot(lesson, useOpenSlotsAllowed());
  const when = showTime ? `${fmtTime(lesson.startsAt)} – ${fmtTime(lesson.endsAt)}` : `${lesson.durationHours} h`;
  return (
    <div
      className={cx(
        'flex items-center gap-3 rounded-card border-2 border-dashed p-4',
        offered ? 'border-emerald-300 bg-success-light/50' : 'border-line bg-surface-light',
      )}
    >
      <span className={cx('grid h-10 w-10 shrink-0 place-items-center rounded-full', offered ? 'bg-white text-success' : 'bg-white text-muted')}>
        <Globe size={17} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className={cx('text-sm font-semibold', offered ? 'text-success-dark' : 'text-secondary')}>
          {offered ? 'Ouvert à la réservation' : 'Créneau non réservé'}
        </p>
        <p className="text-xs text-secondary">
          {when} · {offered ? "visible dans l'espace élève" : 'n’est plus proposé aux élèves'}
        </p>
      </div>
      {offered && <Badge tone="ok">Non réservé</Badge>}
      {entries.length > 0 && (
        <Menu
          entries={entries}
          trigger={(props) => <Button {...props} variant="secondary" iconOnly icon={MoreHorizontal} aria-label="Plus d'actions sur le créneau" />}
        />
      )}
      {dialogs}
    </div>
  );
}

/**
 * Leçon avec un élève. `featured` : version agrandie (leçon en cours ou prochaine, en tête de Ma journée).
 * `showTime` : horaire dans la carte (planning) ; sinon il est porté par la frise.
 */
export function LessonCard({
  lesson,
  featured = false,
  showTime = false,
  highlight,
}: {
  lesson: InstructorLesson;
  featured?: boolean;
  showTime?: boolean;
  /** Pastille de mise en avant (« En cours », « À suivre »). */
  highlight?: string;
}) {
  const openBilan = useUiStore((s) => s.openBilan);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const absence = useMutation({
    mutationFn: () => instructorApi.reportAbsence(lesson.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['today'] });
      void queryClient.invalidateQueries({ queryKey: ['planning'] });
      setAbsenceOpen(false);
      toast('Absence signalée, l’élève est prévenu.');
    },
  });
  const { entries, dialogs } = useLessonActions(lesson);
  const c = lesson.candidate;
  const name = fullName(lesson);
  const state = lessonState(lesson);
  const badge = STATE_BADGE[state];
  const isPast = dayjs().isAfter(dayjs(lesson.endsAt));
  const cancelled = lesson.status === 'cancelled';

  const session = () => c && openBilan({ candidateId: c.id, candidateName: name, lessonId: lesson.id, lessonStartsAt: lesson.startsAt });
  const size = featured ? 'md' : 'sm';

  // Action principale selon le moment : signalement d'absence ou émargement.
  let primary: ReactNode = null;
  const reportable = !cancelled && canReportAbsence(lesson);
  if (reportable)
    primary = (
      <Button variant="secondary" size={size} icon={UserX} onClick={() => setAbsenceOpen(true)}>
        Signaler une absence
      </Button>
    );
  else if (!cancelled && canSignAttendance(lesson))
    primary = (
      <Button variant="primary" size={size} icon={PenLine} onClick={() => setAttendanceOpen(true)}>
        Émargement
      </Button>
    );
  // Livret et résumé de la séance : toujours à côté ; action principale quand il n'y en a pas d'autre.
  const sessionButton = !!c && !cancelled && (
    <Button variant={!primary && (featured || isPast) ? 'primary' : 'secondary'} size={size} icon={BookOpen} onClick={session}>
      Livret et résumé de la séance
    </Button>
  );

  const meta = [
    showTime && `${fmtTime(lesson.startsAt)} – ${fmtTime(lesson.endsAt)}`,
    lesson.lessonType ?? 'Conduite',
    `${lesson.durationHours} h`,
    c && (c.vehicleType === 'automatic' ? 'Boîte auto' : 'Boîte manuelle'),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      aria-label={`Leçon de ${name}`}
      className={cx(
        'rounded-card bg-white shadow-card',
        featured ? 'p-5 ring-2 ring-primary-200 sm:p-6' : 'p-4',
        cancelled && 'opacity-70',
      )}
    >
      <div className={cx('flex gap-3 sm:items-center sm:gap-4', featured ? 'flex-col sm:flex-row' : 'flex-col md:flex-row')}>
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
          <span
            className={cx(
              'relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-avatar font-semibold text-primary-700',
              featured ? 'h-14 w-14 text-base' : 'h-11 w-11 text-[13px]',
            )}
          >
            {c ? initials(c.firstName, c.lastName) : '–'}
            <StudentPhoto candidateId={lesson.candidateId} />
          </span>
          <div className="min-w-0 flex-1">
            {highlight && <p className="mb-0.5 text-eyebrow uppercase text-primary-600">{highlight}</p>}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className={cx('truncate font-semibold text-heading', featured ? 'text-lg' : 'text-[15px]')}>{name}</h3>
              {lesson.bookedOnline && (
                <Badge tone="ok" dot>
                  Réservé en ligne
                </Badge>
              )}
              <Badge tone={badge.tone}>{badge.label}</Badge>
            </div>
            <p className="mt-0.5 text-[13px] text-secondary">{meta}</p>
            {reportable && (
              <p className="mt-0.5 text-xs text-secondary">
                Pas d’émargement : sans absence signalée, présence validée à {fmtTime(absenceDeadline(lesson).toISOString())}.
              </p>
            )}
            {c && (
              <div className="mt-1">
                <PhoneLink phone={c.phoneNumber} name={name} />
              </div>
            )}
          </div>
        </div>
        {(primary || sessionButton || entries.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
            {primary}
            {sessionButton}
            {entries.length > 0 && (
              <Menu
                entries={entries}
                trigger={(props) => (
                  <Button {...props} variant="secondary" size={size} iconOnly icon={MoreHorizontal} aria-label="Plus d'actions" />
                )}
              />
            )}
          </div>
        )}
      </div>
      {attendanceOpen && <AttendanceModal lesson={lesson} onClose={() => setAttendanceOpen(false)} />}
      <ConfirmDialog
        open={absenceOpen}
        title={`Signaler l’absence de ${name} ?`}
        tone="danger"
        confirmLabel="Signaler l’absence"
        pending={absence.isPending}
        error={absence.isError ? failure(absence.error, 'Signalement impossible.') : undefined}
        onConfirm={() => absence.mutate()}
        onCancel={() => {
          setAbsenceOpen(false);
          absence.reset();
        }}
      >
        {lesson.lessonType} de {fmtTime(lesson.startsAt)}. L’élève est prévenu par notification ; le signalement ne se retire pas.
      </ConfirmDialog>
      {dialogs}
    </article>
  );
}
